import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import axios from 'axios';
import configData from '@/data/config.json';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { batchGenerateShortCodes } from '@/lib/shortCode';
import { initializeAnalytics } from '@/lib/analytics';
import { getAccountId } from '@/lib/obfuscate';
import { EncryptEncode, Decrypt, Encrypt } from '@/lib/encryption';
import { ensureAbsoluteHttps } from '@/lib/api';

const ADS_UPDATED_AT_PREFIX = 'ads-updated-at';

const isVehicleHidden = (vehicle: any): boolean =>
  vehicle?.changed?.hidden === true;

const hasValidPrice = (vehicle: any): boolean =>
  Number(vehicle?.price) > 0;

interface FetchAccountIdResult {
  success: boolean;
  accountId?: string;
  message?: string;
}

interface AppDataContextType {
  config: any;
  vehicles: any[];
  allVehicles: any[]; // All vehicles (unfiltered by store)
  accountId: string | null;
  setAccountId: (id: string | null) => void;
  reloadData: () => void;
  fetchAccountIdByDealer: (dealerId: string) => Promise<FetchAccountIdResult>;
  isLoading: boolean;
  isInitialLoading: boolean;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

/**
 * Hook to get vehicles filtered by selected store
 * Complex filtering logic based on unifiedAds and unifiedAdsStore:
 * 
 * 1. unifiedAds: false → Always filter by selected store
 * 2. unifiedAds: true + unifiedAdsStore: null → Show all vehicles from all stores
 * 3. unifiedAds: true + unifiedAdsStore: {id} + selected = unifiedAdsStore → Show all vehicles
 * 4. unifiedAds: true + unifiedAdsStore: {id} + selected ≠ unifiedAdsStore → Filter by selected store
 */
export const useFilteredVehicles = (selectedStore: any) => {
  const { config, allVehicles } = useAppData();

  return useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false; // Default to true
    const unifiedAdsStore = config?.unifiedAdsStore; // Can be null or store ID
    const selectedStoreId = selectedStore?._id;

    // Rule 1: If unifiedAds is false, always filter by selected store
    if (!unifiedAds) {
      if (selectedStoreId) {
        return allVehicles.filter(vehicle => vehicle.store === selectedStoreId);
      }
      return allVehicles;
    }

    // Rule 2: If unifiedAds is true and unifiedAdsStore is null, show all vehicles
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) {
      return allVehicles;
    }

    // Rule 3 & 4: If unifiedAds is true and unifiedAdsStore is set
    if (selectedStoreId === unifiedAdsStore) {
      // Rule 3: Selected store is the unified store → show all vehicles
      return allVehicles;
    } else {
      // Rule 4: Selected store is different → filter by selected store
      if (selectedStoreId) {
        return allVehicles.filter(vehicle => vehicle.store === selectedStoreId);
      }
      return allVehicles;
    }
  }, [config?.unifiedAds, config?.unifiedAdsStore, allVehicles, selectedStore?._id]);
};

interface AppDataProviderProps {
  children: ReactNode;
}

export const AppDataProvider = ({ children }: AppDataProviderProps) => {
  // Get account ID from environment (accessed in obfuscated way)
  const accountIdEnv = getAccountId();

  const [config, setConfig] = useState(configData);
  const [allVehicles, setAllVehicles] = useState([]); // All vehicles (unfiltered)
  const [vehicles, setVehicles] = useState([]); // Will be set from StoreFilteredVehicles component
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [accountId, setAccountId] = useState<string | null>(accountIdEnv);

  const formatEndpoint = (endpoint: string) => {
    if (!endpoint) return '';
    return ensureAbsoluteHttps(endpoint).replace(/\/$/, '');
  };

  const buildApiUrl = (endpoint: string, path: string) => {
    const base = formatEndpoint(endpoint);
    const normalizedPath = (path || '').replace(/^\//, '');
    return normalizedPath ? `${base}/${normalizedPath}` : base;
  };

  const deferUntilPostLoad = (task: () => void, idleTimeout = 5000) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      setTimeout(task, idleTimeout);
      return;
    }

    const runTask = () => {
      const requestIdle = (window as any).requestIdleCallback;
      if (typeof requestIdle === 'function') {
        requestIdle(
          (deadline: any) => {
            if (!deadline || deadline.timeRemaining() > 0 || deadline.didTimeout) {
              task();
              return;
            }
            setTimeout(task, 0);
          },
          { timeout: idleTimeout }
        );
      } else {
        setTimeout(task, idleTimeout);
      }
    };

    if (document.readyState === 'complete') {
      runTask();
    } else {
      window.addEventListener('load', runTask, { once: true });
    }
  };

  const getAdsUpdatedAtKey = (accountIdentifier?: string | null, storeId?: string) => {
    const accountPart = accountIdentifier || 'default';
    const storePart = storeId || 'default';
    return `${ADS_UPDATED_AT_PREFIX}-${accountPart}-${storePart}`;
  };

  const getStoreWithApi = (cfg?: any) => {
    const stores = cfg?.stores ?? config?.stores ?? [];
    return stores.find(
      (store: any) => store?.api?.endpoint
    );
  };

  const fetchAccountIdByDealer = async (dealerId: string): Promise<FetchAccountIdResult> => {
    const trimmedDealerId = dealerId?.trim();

    if (!trimmedDealerId) {
      return {
        success: false,
        message: 'Informe o Dealer ID para continuar.'
      };
    }

    const storeWithApi = getStoreWithApi();

    if (!storeWithApi?.api?.endpoint) {
      return {
        success: false,
        message: 'Nenhuma loja com API configurada para buscar o Dealer ID.'
      };
    }

    try {
      const encryptedQuery = await Encrypt({ dealerId: trimmedDealerId });
      const requestUrl = buildApiUrl(storeWithApi.api.endpoint, 'app/data/accountId');

      const response = await axios.get(requestUrl, {
        params: { data: encryptedQuery },
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000
      });

      const decrypted = await Decrypt(response.data);

      if (decrypted?.status === 200 && decrypted?.accountId) {
        const fetchedAccountId = decrypted.accountId;
        await secureSetItem('accountId', fetchedAccountId);

        return {
          success: true,
          accountId: fetchedAccountId
        };
      }

      return {
        success: false,
        message: 'Dealer ID informado não existe ou não está configurado.'
      };
    } catch (error) {
      console.error('Erro ao buscar accountId pelo Dealer ID:', error);
      return {
        success: false,
        message: 'Erro ao buscar o Dealer ID. Tente novamente.'
      };
    }
  };

  const processAdsData = async (adsData: any[]) => {
    if (!Array.isArray(adsData)) {
      console.warn('Ads data is not an array, skipping update');
      return;
    }

    const processedVehicles = adsData
      .filter((vehicle: any) => !isVehicleHidden(vehicle))
      .map((vehicle: any) => {
        if (vehicle?.changed?.price) {
          const changedPrice = parseFloat(
            vehicle.changed.price.toString().replace(',', '.')
          );

          if (!isNaN(changedPrice)) {
            return {
              ...vehicle,
              price: changedPrice,
              originalPrice: vehicle.price
            };
          }
        }
        return vehicle;
      })
      .filter(hasValidPrice);

    setAllVehicles(processedVehicles);
    setVehicles(processedVehicles);

    try {
      const vehicleIds = processedVehicles.map(v => v.id);
      await batchGenerateShortCodes(vehicleIds);
    } catch (codeError) {
      console.error('Error generating short codes:', codeError);
    }
  };

  const fetchAdsFromS3 = async (adsUrl: string): Promise<any[] | null> => {
    if (!adsUrl) {
      return null;
    }

    try {
      const adsResponse = await fetch(adsUrl, {
        cache: 'no-store'
      });

      if (!adsResponse.ok) {
        throw new Error(`Failed to fetch ads.json: ${adsResponse.status}`);
      }

      const adsData = await adsResponse.json();
      if (!Array.isArray(adsData)) {
        console.warn('ads.json response is not an array, skipping update');
        return null;
      }

      return adsData;
    } catch (error) {
      console.error('Error loading ads data:', error);
      return null;
    }
  };

  const refreshAdsWithChecks = async (latestConfig: any, adsUrl: string, forceReload = false, preloadedS3Ads: any[] | null = null) => {
    if (!adsUrl) {
      return;
    }

    const stores = latestConfig?.stores ?? [];
    const storeWithApi = stores.find((store: any) => store?.api?.endpoint && store?.api?.ads);
    const accountIdentifier = latestConfig?._id || accountId;
    const storageKey = storeWithApi ? getAdsUpdatedAtKey(accountIdentifier, storeWithApi._id) : null;

    // CRITICAL: For initial load (!allVehicles.length), NEVER call API immediately - use S3 only
    // This prevents /app/ads from blocking the critical rendering path
    // But we still check API in background AFTER initial load completes
    const isInitialLoad = !allVehicles.length;
    let shouldReloadAds = forceReload || isInitialLoad; // Reload on initial load OR if forced
    let shouldCheckApi = !forceReload && allVehicles.length > 0; // Check API if we have existing data
    let detectedUpdatedAt: string | null = null;
    let apiAdsData: any[] | null = null;
    // Use preloaded S3 ads if available
    let s3AdsData: any[] | null = preloadedS3Ads && Array.isArray(preloadedS3Ads) ? preloadedS3Ads : null;

    // Check for updates in background (non-blocking, doesn't delay initial load)
    // This runs for subsequent updates (when we already have vehicles)
    if (shouldCheckApi && storeWithApi?.api?.endpoint && storeWithApi?._id) {
      // Use setTimeout to defer this check and avoid blocking
      setTimeout(() => {
        deferUntilPostLoad(() => {
          (async () => {
            try {
              const storedUpdatedAtRaw = storageKey ? await secureGetItem(storageKey) : null;
              const storedUpdatedAt = typeof storedUpdatedAtRaw === 'string'
                ? storedUpdatedAtRaw
                : storedUpdatedAtRaw?.value ?? null;

              const encryptedQuery = await EncryptEncode({ store: storeWithApi._id });
              const updatedAtUrl = buildApiUrl(storeWithApi.api.endpoint, 'app/ads/updatedAt');
              const updatedAtResponse = await axios.get(updatedAtUrl, {
                params: { data: encryptedQuery },
                headers: { 'Content-Type': 'application/json' },
                timeout: 10000 // Reduced timeout for background check
              });

              const decryptedUpdatedAt = await Decrypt(updatedAtResponse.data);
              if (decryptedUpdatedAt?.status === 200 && decryptedUpdatedAt?.updatedAt) {
                const newUpdatedAt = decryptedUpdatedAt.updatedAt;
                
                if (!storedUpdatedAt || new Date(newUpdatedAt).getTime() > new Date(storedUpdatedAt).getTime()) {
                  // Only reload if there's an update - this won't block initial render
                  if (Array.isArray(decryptedUpdatedAt.ads)) {
                    await processAdsData(decryptedUpdatedAt.ads);
                    if (storageKey) {
                      await secureSetItem(storageKey, newUpdatedAt);
                    }
                  }
                }
              }
            } catch (error) {
              // Silently fail - this is a background check
              console.debug('Background ads update check failed:', error);
            }
          })();
        }, 4000);
      }, 1500); // Delay by 2 seconds to not interfere with initial load
      
      // Return early - don't wait for this check
      return;
    }

    // For initial load OR force reload: use S3 data immediately, then check API in background
    // This ensures fast initial render while still getting dynamic updates
    if (shouldReloadAds && storeWithApi?.api?.endpoint && storeWithApi?.api?.ads && storeWithApi?._id && latestConfig?._id) {
      // Use preloaded S3 ads immediately (already loaded in parallel)
      if (preloadedS3Ads && Array.isArray(preloadedS3Ads)) {
        s3AdsData = preloadedS3Ads;
      }

      // CRITICAL: For initial load, delay API call significantly to avoid critical path
      // For subsequent updates, use shorter delay
      const apiDelay = isInitialLoad ? 2000 : 500;

      // Call API in background with delay to avoid critical path
      // Don't wait for this - use S3 data first
      setTimeout(() => {
        deferUntilPostLoad(() => {
          (async () => {
            try {
              const refreshQuery = {
                account: latestConfig._id,
                store: storeWithApi._id
              };

              const encryptedRefresh = await EncryptEncode(refreshQuery);
              const request = {
                method: 'get',
                url: `${storeWithApi?.api?.endpoint}/${storeWithApi.api.ads}?data=${encryptedRefresh}`,
                headers: {
                  'Content-Type': 'application/json'
                },
                timeout: 10000 // Reduced timeout
              };

              const refreshResponse = await axios.request(request);
              const decryptedRefresh = refreshResponse.data;

              if (decryptedRefresh?.status === 200) {
                if (decryptedRefresh?.updatedAt) {
                  detectedUpdatedAt = decryptedRefresh.updatedAt;
                }
                if (Array.isArray(decryptedRefresh?.ads)) {
                  apiAdsData = decryptedRefresh.ads;
                  
                  // Update with API data if different (non-blocking)
                  await processAdsData(decryptedRefresh.ads);
                  if (storageKey && detectedUpdatedAt) {
                    await secureSetItem(storageKey, detectedUpdatedAt);
                  }
                }
              }
            } catch (error) {
              // Silently fail - we already have S3 data
              console.debug('Background API ads refresh failed:', error);
            }
          })();
        }, isInitialLoad ? 9000 : 4000);
      }, apiDelay); // Delay before handing off to idle/load scheduler
      
      // Don't wait for API call - continue with S3 data
    }

    if (!apiAdsData && !s3AdsData && (shouldReloadAds || forceReload)) {
      // Use preloaded S3 ads if available, otherwise fetch
      if (preloadedS3Ads && Array.isArray(preloadedS3Ads)) {
        s3AdsData = preloadedS3Ads;
      } else {
        const fallbackAds = await fetchAdsFromS3(adsUrl);
        if (Array.isArray(fallbackAds)) {
          s3AdsData = fallbackAds;
        }
      }
    }

    const finalAdsData = apiAdsData ?? s3AdsData;

    if (Array.isArray(apiAdsData) && Array.isArray(s3AdsData)) {
      try {
        const apiSnapshot = JSON.stringify(apiAdsData);
        const s3Snapshot = JSON.stringify(s3AdsData);
        if (apiSnapshot !== s3Snapshot) {
          console.info('API ads data differs from S3 ads.json. Keeping API data.');
        }
      } catch (comparisonError) {
        console.warn('Failed to compare API and S3 ads data:', comparisonError);
      }
    }

    if (Array.isArray(finalAdsData)) {
      await processAdsData(finalAdsData);
    }

    if (Array.isArray(finalAdsData) && storageKey && detectedUpdatedAt) {
      try {
        await secureSetItem(storageKey, detectedUpdatedAt);
      } catch (error) {
        console.error('Error saving ads updatedAt:', error);
      }
    }
  };

  // Load accountId on mount and clear localStorage if account changed
  useEffect(() => {
    const loadAccountId = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const queryAccountId = urlParams.get('accountId');

      if (queryAccountId) {
        setAccountId(queryAccountId);
        await secureSetItem('accountId', queryAccountId);
        return;
      }

      const storedId = await secureGetItem('accountId');

      if (storedId) {
        setAccountId(storedId);
      } else if (accountIdEnv) {
        setAccountId(accountIdEnv);
        await secureSetItem('accountId', accountIdEnv);
      }
    };

    loadAccountId();
  }, []);

  // Build URLs - accountId is deobfuscated at runtime only when needed
  const buildUrls = (id: string | null) => {
    if (!id) return { configUrl: '', adsUrl: '' };

    return {
      configUrl: `https://mrapps.s3.amazonaws.com/accounts/${id}/config.json?timestamp=${Date.now()}`,
      adsUrl: `https://mrapps.s3.amazonaws.com/accounts/${id}/ads.json?timestamp=${Date.now()}`
    };
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const { configUrl, adsUrl } = buildUrls(accountId);
      let latestConfigData = config;

      // Prefetch ads.json early to reduce chain delay
      if (adsUrl && typeof document !== 'undefined') {
        const existingPrefetch = document.querySelector(`link[rel="prefetch"][href="${adsUrl}"]`);
        if (existingPrefetch) {
          existingPrefetch.remove();
        }
        
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = adsUrl;
        link.as = 'fetch';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }

      // Load config and ads.json from S3 in parallel for fast initial load
      const promises: Promise<any>[] = [];
      let adsDataFromS3: any[] | null = null;

      // Load config
      if (configUrl) {
        promises.push(
          fetch(configUrl, {
            cache: 'no-store'
          })
            .then(async (configResponse) => {
              if (!configResponse.ok) {
                throw new Error(`Failed to fetch config.json: ${configResponse.status}`);
              }
              
              // CRITICAL: Start preloading LCP image as soon as we get the response
              // Don't wait for JSON parsing - extract banner URL from text before full parse
              // This allows preload to start ~100-300ms earlier, reducing Resource load delay
              const text = await configResponse.text();
              
              // Try to extract banner image URL from JSON text before full parsing
              // Multiple regex patterns to handle different JSON formats
              let bannerImageUrl: string | null = null;
              try {
                // Pattern 1: "banners":[{"image":"url"}
                const pattern1 = /"banners"\s*:\s*\[\s*\{[^}]*"image"\s*:\s*"([^"]+)"/;
                let match = text.match(pattern1);
                
                // Pattern 2: "banners":[{"platform":"web","image":"url"}
                if (!match) {
                  const pattern2 = /"banners"\s*:\s*\[\s*\{[^}]*"platform"\s*:\s*"web"[^}]*"image"\s*:\s*"([^"]+)"/;
                  match = text.match(pattern2);
                }
                
                // Pattern 3: "stores":[{"banners":[{"image":"url"}
                if (!match) {
                  const pattern3 = /"stores"\s*:\s*\[\s*\{[^}]*"banners"\s*:\s*\[\s*\{[^}]*"image"\s*:\s*"([^"]+)"/;
                  match = text.match(pattern3);
                }
                
                if (match && match[1]) {
                  bannerImageUrl = match[1];
                  // Preload immediately - don't wait for full JSON parse
                  if (typeof document !== 'undefined' && bannerImageUrl) {
                    const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${bannerImageUrl}"]`);
                    if (!existingPreload) {
                      const link = document.createElement('link');
                      link.rel = 'preload';
                      link.as = 'image';
                      link.href = bannerImageUrl;
                      link.setAttribute('fetchpriority', 'high');
                      // Handle preload errors silently
                      link.onerror = () => {
                        // Silently handle preload error - image doesn't exist
                        if (link.parentNode) {
                          link.remove();
                        }
                      };
                      document.head.appendChild(link);
                    }
                  }
                }
              } catch (preloadError) {
                // If quick extraction fails, we'll do it after full parse
                // This is non-critical, so we continue
              }
              
              // Now parse the full JSON
              return JSON.parse(text);
            })
            .then((configData) => {
              latestConfigData = configData;
              setConfig(configData);
              initializeAnalytics(configData);
              
              // Preload LCP image (first banner) - ensure it's done even if quick extraction failed
              if (configData?.stores?.[0]?.banners?.length > 0) {
                const firstBanner = configData.stores[0].banners.find((b: any) => 
                  b.platform === 'web' || !b.platform
                ) || configData.stores[0].banners[0];
                
                if (firstBanner?.image && typeof document !== 'undefined') {
                  // Remove existing preload if any (in case quick extraction created one)
                  const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${firstBanner.image}"]`);
                  if (!existingPreload) {
                    const link = document.createElement('link');
                    link.rel = 'preload';
                    link.as = 'image';
                    link.href = firstBanner.image;
                    link.setAttribute('fetchpriority', 'high');
                    // Handle preload errors silently
                    link.onerror = () => {
                      // Silently handle preload error - image doesn't exist
                      if (link.parentNode) {
                        link.remove();
                      }
                    };
                    // Note: Don't set crossOrigin for preload - it's not needed and causes CORS errors
                    // if S3 is not configured for CORS
                    document.head.appendChild(link);
                  }
                }
              }
              
              // Preload store logo and frontage images for better performance
              if (configData?.stores?.[0]) {
                const store = configData.stores[0];
                const imagesToPreload: string[] = [];
                
                if (store.frontage) imagesToPreload.push(store.frontage);
                if (store.icon?.light) imagesToPreload.push(store.icon.light);
                if (store.icon?.dark) imagesToPreload.push(store.icon.dark);
                
                    imagesToPreload.forEach((imageUrl) => {
                      if (imageUrl && typeof document !== 'undefined') {
                        const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${imageUrl}"]`);
                        if (!existingPreload) {
                          const link = document.createElement('link');
                          link.rel = 'preload';
                          link.as = 'image';
                          link.href = imageUrl;
                          // Handle preload errors silently
                          link.onerror = () => {
                            // Silently handle preload error - image doesn't exist
                            if (link.parentNode) {
                              link.remove();
                            }
                          };
                          // Note: Don't set crossOrigin for preload - it's not needed and causes CORS errors
                          // if S3 is not configured for CORS
                          document.head.appendChild(link);
                        }
                      }
                    });
              }
              
              return secureSetItem('cached-config', JSON.stringify(configData));
            })
            .catch((configError) => {
              console.error('Error loading config:', configError);
              return null;
            })
        );
      }

      // Load ads.json from S3 in parallel (fast initial load)
      if (adsUrl) {
        promises.push(
          fetchAdsFromS3(adsUrl)
            .then((adsData) => {
              adsDataFromS3 = adsData;
              // Process S3 ads immediately for fast rendering
              if (Array.isArray(adsData)) {
                processAdsData(adsData);
              }
              return adsData;
            })
            .catch((adsError) => {
              console.error('Error loading ads from S3:', adsError);
              return null;
            })
        );
      }

      // Wait for both to load (fast initial render)
      await Promise.all(promises);

      // Mark initial loading as complete (website can render now)
      setIsLoading(false);
      setIsInitialLoading(false);

      // Now run dynamic ads check in background (non-blocking)
      // This updates ads if API has newer data, but doesn't block rendering
      // Defer by 2 seconds to ensure initial render is complete and avoid critical path
      if (adsUrl && latestConfigData && adsDataFromS3) {
        setTimeout(() => {
          deferUntilPostLoad(() => {
            // Run in background without blocking
            refreshAdsWithChecks(
              latestConfigData, 
              adsUrl, 
              false, // Don't force reload, just check for updates
              adsDataFromS3 // Use pre-loaded S3 data
            ).catch((error) => {
              // Silently fail - we already have S3 data displayed
              console.debug('Background ads update failed:', error);
            });
          }, 8000);
        }, 1500); // Delay before handing off to idle/load scheduler
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Load data on mount and when account ID changes
  useEffect(() => {
    if (accountId) {
      loadData();
    }
  }, [accountId]);

  // Auto-reload data every 10 minutes
  useEffect(() => {
    if (!accountId) return;

    // Set up interval to reload data every 10 minutes (600000ms)
    const intervalId = setInterval(() => {
      loadData();
    }, 600000); // 10 minutes

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [accountId]);

  // Save account ID to localStorage when it changes
  useEffect(() => {
    if (accountId) {
      secureSetItem('accountId', accountId);
    }
  }, [accountId]);

  const reloadData = () => {
    loadData();
  };

  // Add global reload function for debugging
  useEffect(() => {
    (window as any).forceReloadData = () => {
      loadData();
    };
  }, [accountId]);

  return (
    <AppDataContext.Provider value={{
      config,
      vehicles,
      allVehicles,
      accountId,
      setAccountId,
      reloadData,
      fetchAccountIdByDealer,
      isLoading,
      isInitialLoading
    }}>
      {children}
    </AppDataContext.Provider>
  );
};